<template>
  <div
    class="fixed top-0 w-full pip:hidden"
    :class="[(app.navType === 'none' || app.navType === 'sticky') && 'hidden']"
  >
    <div
      class=""
      :style="
        createStyle({
          height: '60px',
          background: `bg-pink-80`,
        })
      "
    ></div>
  </div>

  <div
    v-if="isReady.state"
    class="App flex flex-col stella-page"
    :class="[
      designerBranding.classes,
      app.navType !== 'none' && app.footerType === 'none' && 'min-h-[inherit]',
      alwaysOnScrollbar && 'use-subtle-scrollbars',
    ]"
    :style="[backgroundStyling]"
  >
    <PipHeader />

    <div class="flex flex-col flex-1 w-full mx-auto">
      <template v-if="app.navType !== 'none'">
        <NavBar class="pip:hidden overflow-x-auto">
          <template v-if="!isInlineSidePanel" #sidepanel="{ onClose }">
            <NavHamburgerPanel @close="onClose" />
          </template>
        </NavBar>
        <DefaultNavToolbar v-if="app.stellaNavType === 'default'" />
      </template>

      <CancelationBanner />
      <OutageBanner
        v-if="config.siteOutageBanner"
        :text="config.siteOutageBanner"
        theme="light"
      />
      <EmailVerificationBanner v-if="app.stellaNavType !== 'custom'" />
      <BrandingPreviewBanner />
      <Toast />
      <StellaToast />
      <Loading />

      <div
        class="flex flex-row print:h-full isolate"
        :class="[app.navType !== 'none' && heightClass]"
      >
        <NavHamburgerPanel
          v-if="isInlineSidePanel && app.navType !== 'none'"
          inline
          :shrink="!navState.sidebarVisible"
        />
        <AppRightSidePanel />

        <div
          class="md:flex-1 md:min-h-[80vh] w-full mx-auto isolate overflow-auto h-full"
          :class="[
            isPageFullWidth
              ? isInlineSidePanel &&
                (!navState.sidebarVisible
                  ? 'max-w-[calc(100%-80px)]'
                  : 'max-w-[calc(100%-17rem)]')
              : 'max-w-[1600px]',
          ]"
          :ref="scrollControl.ref"
        >
          <div class="min-h-full">
            <router-view />
          </div>
          <template v-if="app.footerType === 'normal'">
            <StellaFooter class="pip:hidden" />
          </template>
        </div>
      </div>
    </div>

    <div
      class="menu-lower-warp-zone fixed inset-0 pointer-events-none z-40"
    ></div>
    <div class="modals-warp-zone"></div>
    <CommonModals />
    <div
      class="menu-higher-warp-zone fixed inset-0 pointer-events-none z-50"
    ></div>
    <div class="tooltip-warp-zone fixed inset-0 pointer-events-none z-50"></div>

    <!-- dev only stuff -->
    <UserDevToolsRestoreTrigger v-if="!isProd" />

    <StellaModal />
  </div>
</template>

<script lang="ts" setup>
import {
  ref,
  computed,
  defineAsyncComponent,
  h,
  onBeforeMount,
  watch,
  onMounted,
} from 'vue'
import Toast from '/@/core/elements/toast/Toast.vue'
import StellaToast from '/@/core/elements/toast/StellaToast.vue'
import Loading from '/@/core/elements/loading/Loading.vue'
import { useAuth } from '/@/core/auth'
import { useUser } from '/@/data/user'
import { useDesignerBranding } from '/@/data/designer/branding'
import BrandingPreviewBanner from '/@/common/BrandingPreviewBanner.vue'
import { useRoute, useRouter } from 'vue-router'
import { createStyle, useToggle } from '/@/utils/misc'
import { install as installErrorHandlers } from '/@/core/errors'
import CommonModals from './CommonModals.vue'
import { useConfig } from '/@/core/config'
import { useCurrentDesigner } from '/@/data/designer/current'
import PipHeader from './PipHeader.vue'
import { useAppState } from './state'
import DefaultNavToolbar from '/@/common/toolbar/Stella/DefaultNavToolbar.vue'
import StellaModal from '/@/stella/StellaModal/StellaModal.vue'
import { useKeyboard } from '/@/utils/keyboard'
import { installUpdateNotifier } from './updateNotifier'
import { stellaModal } from '/@/stella/StellaModal'
import NavHamburgerPanel from '/@/App/NavHamburgerPanel.vue'
import { useDevice, useDynamicHeight } from '/@/utils/responsive'
import { useNavState } from '/@/stella/StellaNavBar'
import { installPageScrollControl } from '/@/utils/dom'
import { openFeatureSettings } from '/@/utils/modals'
import AppRightSidePanel from './AppRightSidePanel.vue'
import { checkScrollbarConfiguration } from '../utils/dom'

const UserDevToolsRestoreTrigger = defineAsyncComponent(
  () => import('/@/core/dev/RestoreTrigger.vue'),
)
const NavBar = defineAsyncComponent({
  loader: () => import('/@/stella/StellaNavBar/StellaNavBar.vue'),
  // note: this is rendered while the actual component is downloading - it reduces content jumps
  loadingComponent() {
    return h('div', { class: 'h-[60px]' })
  },
})
const StellaFooter = defineAsyncComponent(() => import('/@/App/Footer.vue'))
const OutageBanner = defineAsyncComponent(
  () => import('/@/common/notifications/OutageBanner.vue'),
)
const CancelationBanner = defineAsyncComponent(
  () => import('/@/common/notifications/CancelationBanner.vue'),
)
const EmailVerificationBanner = defineAsyncComponent(
  () => import('/@/common/notifications/EmailVerificationBanner.vue'),
)

const isReady = useToggle()
const auth = useAuth()
const app = useAppState()
const user = useUser()
const route = useRoute()
const router = useRouter()
const config = useConfig()
const { designer, loading: loadingDesigner, isOrgMember } = useCurrentDesigner()
const designerBranding = useDesignerBranding()
const device = useDevice()
const { heightClass } = useDynamicHeight()
const navState = useNavState()

installErrorHandlers()
installUpdateNotifier()

const isProd = computed(() => config.env === 'prod')
const isEmbed = computed(() => config.isEmbed)

const scrollControl = installPageScrollControl()

watch(
  () => navState.colorConfig,
  config => {
    let metaTag = document.querySelector('meta[name="theme-color"]')

    if (!metaTag) {
      metaTag = document.createElement('meta')
      metaTag.setAttribute('name', 'theme-color')
      document.head.appendChild(metaTag)
    }
    metaTag.setAttribute('content', config.bgColor)
  },
  {
    immediate: true,
  },
)

const isStellaPage = computed(() => app.theme === 'stella')
const isPageFullWidth = computed(() => app.fullWidth)
const isInlineSidePanel = computed(
  () => !isEmbed.value && app.stellaNavType === 'default' && device.isAtLeastLg,
)

const backgroundStyling = computed(() => {
  return isStellaPage.value
    ? createStyle({
        'background-color': '#fefdfa',
        color: '#272727',
      })
    : createStyle({
        'background-color': '#f8f6f2',
        'background-image':
          "url('https://cdn.spoak.com/assets/page-bg.6994e195.jpg')",
        'background-size': '100% auto',
        'background-repeat': 'repeat',
        color: '#243746',
      })
})

const keyboardApi = useKeyboard()

onMounted(() => {
  keyboardApi.register()
})

watch(
  isInlineSidePanel,
  inline => {
    if (inline) {
      navState.sidebarVisible = true
    }
  },
  {
    immediate: true,
  },
)

watch([isInlineSidePanel, () => route.path], ([inline]) => {
  if (inline) return

  navState.sidebarVisible = false
})

onBeforeMount(async () => {
  keyboardApi.unregister()

  const cookie = document.cookie
  await auth.checkSession(user, cookie)

  if (!route.meta?.noAuth) {
    if (!user.isLoggedIn) {
      await auth.logIn(user, cookie)
    } else if (!user.current?.designer) {
      // User logged in but there is no associated designer. Possible with social login (i.e. via Google) in Auth0.
      router.replace({ name: 'require-login' })
    } else if (user.current.designer?.status === 'INACTIVE') {
      router.push({
        name: 'signup',
        query: { type: 'REACTIVATION', sp_ft2: 'true' },
      })
    }
  }
  isReady.on()
})

watch(
  [() => designer.value, loadingDesigner, isOrgMember],
  ([d, l, orgMember]) => {
    if (!d || l || orgMember) return

    const projectId = route.params.projectId ?? null
    if (
      !d.onboardingState &&
      d.tier === 'FREE' &&
      !d.freeTierV1Experiment &&
      route.name !== 'onboarding-basic' &&
      projectId
    ) {
      router.replace({
        name: 'onboarding-basic',
        query: { projectId },
      })
    } else if (
      (!d.onboardingState && (d.tier !== 'FREE' || d.freeTierV1Experiment)) ||
      route.query.showOnboarding
    ) {
      if (route.query.showOnboarding) {
        const query = { ...route.query }
        delete query.showOnboarding
        router.push({ query })
      }
      stellaModal.open({
        content: defineAsyncComponent(
          () => import('/@/pages/Onboarding/Full/FormModalContent.vue'),
        ),
        strict: true,
      })
    }
  },
  {
    immediate: true,
  },
)

watch(
  () => route.meta,
  meta => {
    app.theme = meta.theme ?? 'default'
    app.navTheme = meta.navTheme ?? 'light'
    app.fullWidth = meta.fullWidth ?? false
    app.navType = meta.navType ?? 'sticky'
    app.stellaNavType = meta.stellaNavType ?? 'default'
    app.footerType = meta.footerType ?? 'normal'

    scrollControl.scroll({ top: 0, mode: 'just self' })
  },
  { immediate: true },
)

if (route.query.showSignQuiz) {
  stellaModal.open({
    content: defineAsyncComponent(
      () =>
        import(
          '/@/common/modals/SpoakSignQuizModal/SpoakSignQuizModalContent.vue'
        ),
    ),
  })
}

if (!isProd.value) {
  window.addEventListener('keypress', e => {
    if (e.shiftKey && e.ctrlKey && e.key === 'K') {
      openFeatureSettings()
    }
  })
}

// check if scrollbars are always visible
const alwaysOnScrollbar = ref(false)
if (checkScrollbarConfiguration() === 'always-visible') {
  alwaysOnScrollbar.value = true
}
</script>

<style lang="scss">
// todo: we can probably update these values by calculating exact measurements at runtime
html {
  --nav-height: 109px;
  --footer-height: 329px;
  --footer-peak-height: 130px;
}
</style>

<style lang="scss">
@import '../core/styles/lib';

#app {
  @include minAvailableHeight();

  &.-no-scroll {
    @include maxAvailableHeight();
  }
}
</style>

<style lang="scss">
@import '../core/styles/lib';

$track-w: 12.5em;
$track-h: 0.25em;
$thumb-d: 1em;
$track-c: #ccc;
$filll-c: #95a;
@mixin track() {
  box-sizing: border-box;
  border: none;
  width: $track-w;
  height: $track-h;
  background: #ccc;
}
@mixin thumb() {
  box-sizing: border-box;
  border: none;
  width: $thumb-d;
  height: $thumb-d;
  border-radius: 50%;
  background: #243746;
}
#app:not(.-is-ff) {
  [type='range'] {
    padding: 0;
    height: $thumb-d;
    background: transparent;
    font:
      1em/1 arial,
      sans-serif;
    &,
    &::-webkit-slider-thumb {
      -webkit-appearance: none;
    }
    &::-webkit-slider-runnable-track {
      @include track;
    }
    &::-moz-range-track {
      @include track;
    }
    &::-webkit-slider-thumb {
      margin-top: 0.5 * ($track-h - $thumb-d);
      @include thumb;
    }
    &::-moz-range-thumb {
      @include thumb;
    }
  }
}
</style>

<style lang="scss">
@import '../core/styles/lib';

.visible-scrollbar {
  &::-webkit-scrollbar {
    width: 11px;
  }
  & {
    scrollbar-width: thin;
    scrollbar-color: $c-gray-deep $c-gray-barely;
  }
  &::-webkit-scrollbar-track {
    background: $c-gray-barely;
  }
  &::-webkit-scrollbar-thumb {
    background-color: $c-gray-deep;
    border-radius: 6px;
    border: 3px solid $c-gray-barely;
  }
}
</style>

<style>
.App {
  --branding-navigation: v-bind('designerBranding.cssValues.navigation');
  --branding-navigation-text: v-bind(
    'designerBranding.cssValues.navigationText'
  );
  --branding-navigation-logo: v-bind(
    'designerBranding.cssValues.navigationLogo'
  );
  --branding-accent: v-bind('designerBranding.cssValues.accent');
}
</style>
